import { Select } from '@byecode/ui'
import type { IndependentAction, SourceRecordConfig } from '@lighthouse/core'
import { DataSourceType, FilterMode, FlowSelectSourceType } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import {
    fromUpstreamOptions,
    getAppointField,
    getImageVariableOption,
    getUpstreamNodeOption,
    getUpstreamRealDsId,
    getViewColumns,
    ListItem4ByecodeUi,
    notDataSettingInnerTypes,
    SelectDataSource,
    useAtomAction,
    useAtomData,
    ViewFieldFilterConfigure,
    ViewSortConfigure
} from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useDeferredValue, useMemo } from 'react'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { ListItemMenu } from '@/components/BlockSettings/Common/ListItemMenu'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import * as SC from '../../styles'

export interface SelectRecordControlledProps {
    value: SourceRecordConfig
    onChange: (value: SourceRecordConfig) => void
    upstreamNodesForVariableSelect?: FlowNode[]
    upstreamNodesForSource?: FlowNode[]
    allParentActions?: IndependentAction[]
    upstreamRecordIsFilterAndSort?: boolean
    disableOmitAggregate?: boolean
    disableUpstreamRecord?: boolean
}

export const SelectRecordControlled: React.FC<SelectRecordControlledProps> = ({
    value,
    onChange,
    upstreamNodesForVariableSelect,
    upstreamNodesForSource,
    allParentActions,
    upstreamRecordIsFilterAndSort = true,
    disableOmitAggregate,
    disableUpstreamRecord
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const { type } = useFlowContainerContext()
    const isInWorkflow = !!type
    const workflowList = useAtomData(workflowListAtom)

    const { selectType, dataSourceId: dsId, nodeId, filter, sorts } = value

    const dataSourceOmitAggregate = useMemo(
        () => dataSourceList.filter(ds => disableOmitAggregate || ds.type !== DataSourceType.aggregateDataSource),
        [dataSourceList, disableOmitAggregate]
    )

    const fromRecordOptions = useMemo(() => {
        if (disableUpstreamRecord) {
            return fromUpstreamOptions.filter(item => item.value === FlowSelectSourceType.DATASOURCE)
        }
        return fromUpstreamOptions
    }, [disableUpstreamRecord])

    const dataSource = useMemo(() => {
        if (selectType === FlowSelectSourceType.DATASOURCE) {
            return find(item => item.id === dsId, dataSourceList)
        }
        if (!upstreamRecordIsFilterAndSort) {
            return
        }
        const upstreamDsId = getUpstreamRealDsId(nodeId, upstreamNodesForSource)
        return find(item => item.id === upstreamDsId, dataSourceList)
    }, [dataSourceList, dsId, nodeId, selectType, upstreamNodesForSource, upstreamRecordIsFilterAndSort])

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInWorkflow ? '' : s?.pageId || ''), [isInWorkflow])
    )

    const nodeOptions = useMemo(
        () =>
            getUpstreamNodeOption({
                allParentNodes: upstreamNodesForSource || [],
                dataSourceList
            }),
        [dataSourceList, upstreamNodesForSource]
    )

    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const columns = useMemo(
        () =>
            getViewColumns({
                tableProps: dataSource?.viewOptions?.tableProps || [],
                schema: dataSource?.schema || {}
            }),
        [dataSource?.schema, dataSource?.viewOptions?.tableProps]
    )

    const primaryField = useMemo(() => {
        if (!dataSource) {
            return columns[0]?.fieldId
        }
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))

    const renderSetRecordContent = useMemo(() => {
        if (selectType === FlowSelectSourceType.DATASOURCE) {
            return (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>选择数据表</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <SelectDataSource
                            withinPortal
                            dataSourceList={dataSourceOmitAggregate}
                            value={dsId}
                            onChange={val => {
                                if (dsId === val) {
                                    return
                                }
                                onChange({
                                    ...value,
                                    fields: [],
                                    dataSourceId: val
                                })
                            }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )
        }

        return (
            <ListItem4ByecodeUi>
                <SC.FormItemLabelWrapper>
                    <SC.FormItemLabel required>选择上游节点</SC.FormItemLabel>
                </SC.FormItemLabelWrapper>
                <SC.FormItemContent>
                    <Select
                        disabledPortal={false}
                        value={nodeId}
                        notFound="找不到节点"
                        options={nodeOptions}
                        onChange={val => {
                            if (nodeId === val) {
                                return
                            }
                            onChange({
                                ...value,
                                fields: [],
                                nodeId: val
                            })
                        }}
                    />
                </SC.FormItemContent>
            </ListItem4ByecodeUi>
        )
    }, [dataSourceOmitAggregate, dsId, nodeId, nodeOptions, onChange, selectType, value])

    return (
        <>
            <ListItem4ByecodeUi>
                <SC.FormItemLabelWrapper>
                    <SC.FormItemLabel required>记录来自</SC.FormItemLabel>
                </SC.FormItemLabelWrapper>
                <SC.FormItemContent>
                    <Select
                        value={selectType}
                        options={fromRecordOptions}
                        onChange={val => {
                            if (val === selectType) {
                                return
                            }
                            onChange({
                                ...value,
                                nodeId: '',
                                dataSourceId: '',
                                selectType: val as FlowSelectSourceType
                            })
                        }}
                    />
                </SC.FormItemContent>
            </ListItem4ByecodeUi>

            {renderSetRecordContent}

            {dataSource && (
                <>
                    <ListItemMenu
                        width="auto"
                        label="查询条件"
                        enableHover={false}
                        withinPortal
                        value={filter?.expression?.conditions?.length ? `已设 ${filter.expression.conditions.length} 个条件` : '未设置'}
                        popupContent={
                            <ViewFieldFilterConfigure
                                dataSource={dataSource}
                                dataSourceList={dataSourceList}
                                availableFieldBlocksInfo={isInWorkflow ? undefined : availableFieldBlocksInfo}
                                availableFilterBlocksInfo={isInWorkflow ? undefined : availableFilterBlocksInfo}
                                pagePathParameter={pagePathParameter}
                                pageQueryParameter={pageQueryParameter}
                                pageVariables={pageVariables}
                                pageStateParameter={pageStateParameter}
                                workflowList={workflowList}
                                upstreamNodes={type && upstreamNodesForVariableSelect}
                                allUpstreamNodes={type && upstreamNodesForVariableSelect}
                                allParentActions={allParentActions}
                                viewDsId={viewDsId}
                                formDsId={formDsId}
                                viewType={viewType}
                                isUseImage={isImage}
                                paramsMode={FilterMode.CUSTOM}
                                fieldMode={FilterMode.CUSTOM}
                                columns={columns}
                                primaryField={primaryField}
                                filter={filter}
                                noSettingInnerType={notDataSettingInnerTypes}
                                onFilter={nextFilter => onChange({ ...value, filter: nextFilter })}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            />
                        }
                    />

                    <ListItemMenu
                        width="auto"
                        label="排序规则"
                        enableHover={false}
                        withinPortal
                        value={sorts?.length ? `已设${String(sorts.length)}个条件` : '未设置'}
                        popupContent={
                            <ViewSortConfigure
                                columns={columns}
                                primaryField="ID"
                                noSettingInnerType={notDataSettingInnerTypes}
                                isShowDsName={dataSource?.type === DataSourceType.joinDataSource}
                                sorters={sorts}
                                onChangeSorter={nextSorts => onChange({ ...value, sorts: nextSorts })}
                            />
                        }
                    />
                </>
            )}
        </>
    )
}
